import { useState, useEffect } from 'react';
import { TrendingUp, BookOpen, DollarSign, Clock, Download, Globe, UserCheck, Briefcase } from 'lucide-react';
import * as XLSX from 'xlsx';
import api from '../../services/api';

const HOLD_STATUS_COLORS = {
  active:    { bg: '#fef3c7', color: '#d97706' },
  expired:   { bg: '#fee2e2', color: '#dc2626' },
  converted: { bg: '#dcfce7', color: '#16a34a' },
};

const SOURCE_META = {
  online:  { label: 'Online',  bg: '#dbeafe', color: '#1e40af', icon: Globe },
  agent:   { label: 'Agent',   bg: '#fef3c7', color: '#92400e', icon: UserCheck },
  offline: { label: 'Offline', bg: '#f3e8ff', color: '#6b21a8', icon: Briefcase },
};

const Reports = () => {
  const [activeTab, setActiveTab] = useState('revenue');

  const [revenueData, setRevenueData]   = useState([]);
  const [bookingsData, setBookingsData] = useState(null);
  const [year, setYear]                 = useState(new Date().getFullYear());
  const [loading, setLoading]           = useState(true);

  const [holdsData, setHoldsData]         = useState(null);
  const [holdCounts, setHoldCounts]       = useState(null);
  const [holdsLoading, setHoldsLoading]   = useState(false);
  const [holdStatusFilter, setHoldStatusFilter] = useState('');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/reports/revenue?year=${year}`),
      api.get('/reports/bookings'),
    ]).then(([revRes, bookRes]) => {
      setRevenueData(revRes.data.monthly || []);
      setBookingsData(bookRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [year]);

  useEffect(() => {
    if (activeTab !== 'holds') return;
    setHoldsLoading(true);
    const params = holdStatusFilter ? `?status=${holdStatusFilter}` : '';
    api.get(`/reports/holds${params}`)
      .then(res => { setHoldsData(res.data.holds || []); setHoldCounts(res.data.counts); })
      .catch(() => {})
      .finally(() => setHoldsLoading(false));
  }, [activeTab, holdStatusFilter]);

  const totalRevenue      = revenueData.reduce((s, m) => s + Number(m.revenue || 0), 0);
  const totalBookingsYear = revenueData.reduce((s, m) => s + Number(m.bookings || 0), 0);
  const maxRevenue        = Math.max(...revenueData.map(m => Number(m.revenue || 0)), 1);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const fmtDate     = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

  // ── Excel export ──────────────────────────────────────────────────────────────
  const exportExcel = () => {
    const wb = XLSX.utils.book_new();

    // Sheet 1: Monthly Revenue
    const revRows = revenueData.map(r => ({
      Month: r.month,
      Bookings: Number(r.bookings),
      'Revenue (₹)': Number(r.revenue),
    }));
    revRows.push({ Month: 'TOTAL', Bookings: totalBookingsYear, 'Revenue (₹)': totalRevenue });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(revRows), 'Monthly Revenue');

    // Sheet 2: By Room
    if (bookingsData?.by_room?.length) {
      const roomRows = bookingsData.by_room.map(r => ({
        Room: r.name,
        'Total Bookings': Number(r.total_bookings),
        'Revenue (₹)': Number(r.total_revenue),
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(roomRows), 'By Room');
    }

    // Sheet 3: By Agent
    if (bookingsData?.by_agent?.length) {
      const agentRows = bookingsData.by_agent.map(a => ({
        Agent: a.agent_name,
        'Bookings Created': Number(a.bookings_created),
        'Commission Earned (₹)': Number(a.commissions_earned),
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(agentRows), 'By Agent');
    }

    // Sheet 4: By Source
    if (bookingsData?.by_source?.length) {
      const srcRows = bookingsData.by_source.map(s => ({
        Source: (SOURCE_META[s.source]?.label || s.source),
        'Total Bookings': Number(s.total_bookings),
        'Revenue (₹)': Number(s.total_revenue),
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(srcRows), 'By Source');
    }

    XLSX.writeFile(wb, `buranshikes-report-${year}.xlsx`);
  };

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={s.pageTitle}>Reports & Analytics</h1>
            <p style={s.pageSubtitle}>Business performance overview</p>
          </div>
          {!loading && (
            <button onClick={exportExcel} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 10, border: '1.5px solid #e5e7eb', backgroundColor: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', color: '#374151' }}>
              <Download size={15} /> Export Excel
            </button>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '2px solid #e5e7eb', paddingBottom: 0 }}>
        {[
          { key: 'revenue', label: 'Revenue & Bookings' },
          { key: 'sources', label: 'Booking Sources' },
          { key: 'holds',   label: 'Room Holds' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            padding: '10px 20px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer',
            fontSize: 14, fontWeight: 700,
            color: activeTab === tab.key ? '#16a34a' : '#6b7280',
            borderBottom: `2px solid ${activeTab === tab.key ? '#16a34a' : 'transparent'}`,
            marginBottom: -2, transition: 'color 0.15s, border-color 0.15s',
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Revenue & Bookings Tab ── */}
      {activeTab === 'revenue' && (
        loading ? <p style={{ color: '#6b7280' }}>Loading reports...</p> : (
          <>
            <div style={s.topRow}>
              <div style={s.summaryCard}>
                <div style={{ ...s.iconWrap, backgroundColor: '#d1fae5' }}><DollarSign size={24} color="#16a34a" /></div>
                <div>
                  <p style={s.summaryVal}>₹{totalRevenue.toLocaleString()}</p>
                  <p style={s.summaryLabel}>{year} Total Revenue</p>
                </div>
              </div>
              <div style={s.summaryCard}>
                <div style={{ ...s.iconWrap, backgroundColor: '#dbeafe' }}><BookOpen size={24} color="#3b82f6" /></div>
                <div>
                  <p style={s.summaryVal}>{totalBookingsYear}</p>
                  <p style={s.summaryLabel}>{year} Total Bookings</p>
                </div>
              </div>
              <div style={s.summaryCard}>
                <div style={{ ...s.iconWrap, backgroundColor: '#ede9fe' }}><TrendingUp size={24} color="#8b5cf6" /></div>
                <div>
                  <p style={s.summaryVal}>₹{totalBookingsYear ? Math.round(totalRevenue / totalBookingsYear).toLocaleString() : 0}</p>
                  <p style={s.summaryLabel}>Avg. Booking Value</p>
                </div>
              </div>
            </div>

            <div style={s.card}>
              <div style={s.cardHeader}>
                <h2 style={s.sectionTitle}>Monthly Revenue — {year}</h2>
                <select style={s.yearSelect} value={year} onChange={(e) => setYear(Number(e.target.value))}>
                  {[2023,2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div style={s.barChart}>
                {months.map((m, i) => {
                  const row = revenueData.find(r => Number(r.month_num) === i + 1) || {};
                  const rev = Number(row.revenue || 0);
                  const pct = Math.round((rev / maxRevenue) * 100);
                  return (
                    <div key={m} style={s.barCol}>
                      <p style={s.barValue}>₹{rev > 0 ? (rev / 1000).toFixed(0) + 'k' : '0'}</p>
                      <div style={s.barTrack}><div style={{ ...s.bar, height: `${pct}%` }} /></div>
                      <p style={s.barLabel}>{m}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={s.row}>
              {bookingsData?.by_room?.length > 0 && (
                <div style={s.card}>
                  <h2 style={s.sectionTitle}>Bookings by Room</h2>
                  <div style={s.tableWrap}>
                    <div style={s.tableHead2}><span>Room</span><span>Bookings</span><span>Revenue</span></div>
                    {bookingsData.by_room.map((r, i) => (
                      <div key={i} style={s.tableRow2}>
                        <span>{r.name}</span>
                        <span style={s.countBadge}>{r.total_bookings}</span>
                        <span style={{ color: '#16a34a', fontWeight: '600' }}>₹{Number(r.total_revenue || 0).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {bookingsData?.by_agent?.length > 0 && (
                <div style={s.card}>
                  <h2 style={s.sectionTitle}>Bookings by Agent</h2>
                  <div style={s.tableWrap}>
                    <div style={s.tableHead2}><span>Agent</span><span>Bookings</span><span>Commission</span></div>
                    {bookingsData.by_agent.map((a, i) => (
                      <div key={i} style={s.tableRow2}>
                        <span>{a.agent_name}</span>
                        <span style={s.countBadge}>{a.bookings_created}</span>
                        <span style={{ color: '#f59e0b', fontWeight: '600' }}>₹{Number(a.commissions_earned || 0).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {bookingsData?.recent_bookings?.length > 0 && (
              <div style={s.card}>
                <h2 style={s.sectionTitle}>Recent Bookings</h2>
                <div style={s.tableHead3}><span>Reference</span><span>Customer</span><span>Room</span><span>Check-in</span><span>Amount</span><span>Status</span></div>
                {bookingsData.recent_bookings.map(b => (
                  <div key={b.id} style={s.tableRow3}>
                    <span style={{ color: '#16a34a', fontWeight: '600', fontSize: '13px' }}>{b.booking_reference}</span>
                    <span>{b.customer_name}</span>
                    <span>{b.room_name}</span>
                    <span>{b.check_in}</span>
                    <span>₹{Number(b.total_amount).toLocaleString()}</span>
                    <span style={{ ...s.badge, backgroundColor: '#d1fae5', color: '#065f46' }}>{b.booking_status}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )
      )}

      {/* ── Booking Sources Tab ── */}
      {activeTab === 'sources' && (
        loading ? <p style={{ color: '#6b7280' }}>Loading...</p> : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20, marginBottom: 28 }}>
              {['online','agent','offline'].map(src => {
                const meta   = SOURCE_META[src];
                const row    = bookingsData?.by_source?.find(r => r.source === src) || {};
                const IconC  = meta.icon;
                return (
                  <div key={src} style={{ backgroundColor: 'white', borderRadius: 12, padding: '22px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ width: 48, height: 48, borderRadius: 12, backgroundColor: meta.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <IconC size={22} color={meta.color} />
                    </div>
                    <div>
                      <p style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: 0 }}>{Number(row.total_bookings || 0)}</p>
                      <p style={{ fontSize: 13, color: '#6b7280', margin: '2px 0 0' }}>{meta.label} Bookings</p>
                      <p style={{ fontSize: 13, fontWeight: 700, color: meta.color, margin: '2px 0 0' }}>₹{Number(row.total_revenue || 0).toLocaleString()}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={s.card}>
              <h2 style={s.sectionTitle}>Source Breakdown</h2>
              {(() => {
                const total = bookingsData?.by_source?.reduce((a, r) => a + Number(r.total_bookings), 0) || 0;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 16 }}>
                    {['online','agent','offline'].map(src => {
                      const meta = SOURCE_META[src];
                      const row  = bookingsData?.by_source?.find(r => r.source === src) || {};
                      const cnt  = Number(row.total_bookings || 0);
                      const pct  = total > 0 ? Math.round((cnt / total) * 100) : 0;
                      return (
                        <div key={src}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>{meta.label}</span>
                            <span style={{ fontSize: 14, fontWeight: 700, color: meta.color }}>{cnt} bookings ({pct}%)</span>
                          </div>
                          <div style={{ height: 10, borderRadius: 99, backgroundColor: '#f3f4f6' }}>
                            <div style={{ height: '100%', borderRadius: 99, backgroundColor: meta.color, width: `${pct}%`, transition: 'width 0.5s ease' }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </>
        )
      )}

      {/* ── Room Holds Tab ── */}
      {activeTab === 'holds' && (
        holdsLoading ? <p style={{ color: '#6b7280' }}>Loading holds...</p> : (
          <>
            {holdCounts && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 }}>
                {[
                  { label: 'Total Holds',  value: holdCounts.total,     bg: '#dbeafe', color: '#1d4ed8' },
                  { label: 'Active',       value: holdCounts.active,    bg: '#fef3c7', color: '#d97706' },
                  { label: 'Expired',      value: holdCounts.expired,   bg: '#fee2e2', color: '#dc2626' },
                  { label: 'Converted',    value: holdCounts.converted, bg: '#dcfce7', color: '#16a34a' },
                ].map(item => (
                  <div key={item.label} style={{ backgroundColor: 'white', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 10, backgroundColor: item.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Clock size={20} color={item.color} />
                    </div>
                    <div>
                      <p style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: 0 }}>{item.value}</p>
                      <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>{item.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
              <label style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>Filter by status:</label>
              <select value={holdStatusFilter} onChange={e => setHoldStatusFilter(e.target.value)} style={{ padding: '8px 14px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', cursor: 'pointer' }}>
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="converted">Converted</option>
              </select>
            </div>
            <div style={s.card}>
              <h2 style={{ ...s.sectionTitle, marginBottom: 16 }}>Hold History</h2>
              {!holdsData?.length ? (
                <p style={{ color: '#6b7280', fontSize: 14 }}>No holds found.</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <div style={{ minWidth: 900 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.7fr 1fr 0.8fr 0.8fr 0.8fr 0.8fr 0.8fr 0.7fr 0.9fr', gap: 10, padding: '10px 14px', backgroundColor: '#f3f4f6', borderRadius: 8, fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>
                      <span>Room</span><span>Room#</span><span>Held By</span><span>Type</span><span>Check-in</span><span>Check-out</span><span>Created</span><span>Expires</span><span>Status</span><span>Booking Ref</span>
                    </div>
                    {holdsData.map(h => {
                      const sc = HOLD_STATUS_COLORS[h.status] || {};
                      return (
                        <div key={h.id} style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.7fr 1fr 0.8fr 0.8fr 0.8fr 0.8fr 0.8fr 0.7fr 0.9fr', gap: 10, padding: '13px 14px', borderBottom: '1px solid #f3f4f6', fontSize: 13, color: '#374151', alignItems: 'center' }}>
                          <span style={{ fontWeight: 600 }}>{h.room_name}</span>
                          <span>{h.room_number || '—'}</span>
                          <span>{h.held_by_name}</span>
                          <span style={{ textTransform: 'capitalize' }}>{h.held_by_type}</span>
                          <span>{fmtDate(h.check_in)}</span>
                          <span>{fmtDate(h.check_out)}</span>
                          <span style={{ fontSize: 12 }}>{fmtDateTime(h.created_at)}</span>
                          <span style={{ fontSize: 12 }}>{fmtDateTime(h.expires_at)}</span>
                          <span style={{ padding: '3px 10px', borderRadius: 20, backgroundColor: sc.bg, color: sc.color, fontSize: 11, fontWeight: 700, textTransform: 'capitalize', textAlign: 'center' }}>{h.status}</span>
                          <span style={{ color: '#16a34a', fontWeight: 600, fontSize: 12 }}>{h.booking_reference || '—'}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </>
        )
      )}
    </div>
  );
};

const s = {
  container:    { padding: '32px', backgroundColor: '#f9fafb', minHeight: '100vh' },
  header:       { marginBottom: '28px' },
  pageTitle:    { fontSize: '32px', fontWeight: 'bold', color: '#111827', marginBottom: '6px' },
  pageSubtitle: { fontSize: '16px', color: '#6b7280' },
  topRow:       { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '28px' },
  summaryCard:  { backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', gap: '16px' },
  iconWrap:     { width: '52px', height: '52px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  summaryVal:   { fontSize: '26px', fontWeight: 'bold', color: '#111827', marginBottom: '4px' },
  summaryLabel: { fontSize: '14px', color: '#6b7280' },
  card:         { backgroundColor: 'white', borderRadius: '12px', padding: '28px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '24px' },
  cardHeader:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  sectionTitle: { fontSize: '20px', fontWeight: '600', color: '#111827' },
  yearSelect:   { padding: '8px 14px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', outline: 'none', cursor: 'pointer' },
  barChart:     { display: 'flex', alignItems: 'flex-end', gap: '8px', height: '200px', padding: '0 8px' },
  barCol:       { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', height: '100%' },
  barValue:     { fontSize: '11px', color: '#9ca3af', textAlign: 'center', height: '16px' },
  barTrack:     { flex: 1, width: '100%', backgroundColor: '#f3f4f6', borderRadius: '4px', display: 'flex', alignItems: 'flex-end' },
  bar:          { width: '100%', backgroundColor: '#16a34a', borderRadius: '4px', minHeight: '4px', transition: 'height 0.5s ease' },
  barLabel:     { fontSize: '12px', color: '#6b7280', textAlign: 'center' },
  row:          { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' },
  tableWrap:    { display: 'flex', flexDirection: 'column', gap: '2px' },
  tableHead2:   { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', padding: '8px 12px', backgroundColor: '#f3f4f6', borderRadius: '6px', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' },
  tableRow2:    { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', padding: '12px', borderBottom: '1px solid #f3f4f6', fontSize: '14px', color: '#374151', alignItems: 'center' },
  countBadge:   { display: 'inline-block', padding: '2px 10px', backgroundColor: '#dbeafe', color: '#1d4ed8', borderRadius: '12px', fontSize: '13px', fontWeight: '600', textAlign: 'center' },
  tableHead3:   { display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 0.8fr 0.8fr 0.7fr', gap: '12px', padding: '10px 16px', backgroundColor: '#f3f4f6', borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px' },
  tableRow3:    { display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 0.8fr 0.8fr 0.7fr', gap: '12px', padding: '14px 16px', borderBottom: '1px solid #f3f4f6', fontSize: '14px', color: '#374151', alignItems: 'center' },
  badge:        { padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', textTransform: 'capitalize', textAlign: 'center' },
};

export default Reports;
